/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
'use client';

import { CaretSortIcon, CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons';
import { useState, ComponentPropsWithoutRef } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type PopoverTriggerProps = ComponentPropsWithoutRef<typeof PopoverTrigger>;

interface NavSwitcherProps extends PopoverTriggerProps {
    currentKey: string;
    items: { key: string; value: string }[];
    handleSwitch: (message?: string | number) => void;
    handleCreate: (message?: string) => void;
    label: string; // The name that displays (eg. select a <x>)
    popOverWidth?: string; //Width of the dropdown, default 200px
}

const NavSwitcher = ({
    className,
    items,
    currentKey,
    handleSwitch,
    handleCreate,
    label,
    popOverWidth,
}: NavSwitcherProps) => {
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label="Select a team"
                    className={cn(`${popOverWidth ? popOverWidth : 'w-[200px]'} justify-between`, className)}
                >
                    {/* <Avatar className="mr-2 h-5 w-5">
                        <AvatarImage src="Harley" alt={current} />
                        <AvatarFallback>SC</AvatarFallback>
                    </Avatar> */}
                    {items.find((v) => v.key === currentKey)?.value || 'Select a project'}
                    <CaretSortIcon className="ml-auto h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={`${popOverWidth ? popOverWidth : 'w-[200px]'}  p-0`}>
                <Command>
                    <CommandList>
                        <CommandInput placeholder={`Search ${label}`} />
                        <CommandEmpty>No {label} found.</CommandEmpty>
                        {items &&
                            items.map(({ key, value }) => (
                                <CommandItem
                                    key={key}
                                    onSelect={() => {
                                        handleSwitch(key);
                                        setOpen(false);
                                    }}
                                    className="text-sm"
                                >
                                    <Avatar className="mr-2 h-5 w-5">
                                        {value.length > 0 && <AvatarFallback>{value[0].toUpperCase()}</AvatarFallback>}
                                    </Avatar>
                                    {value}
                                    <CheckIcon
                                        className={cn(
                                            'ml-auto h-4 w-4',
                                            currentKey === key.toString() ? 'opacity-100' : 'opacity-0',
                                        )}
                                    />
                                </CommandItem>
                            ))}
                    </CommandList>
                    <CommandSeparator />
                    <CommandList>
                        <CommandGroup>
                            <CommandItem
                                onSelect={() => {
                                    setOpen(false);
                                    handleCreate();
                                }}
                            >
                                <PlusCircledIcon className="mr-2 h-5 w-5" />
                                Create new {label}
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default NavSwitcher;
